import React from "react";
import { motion } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";
import { Icon } from "@/lib/icons";

const NEW_HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_premium-relocate/artifacts/vvli9e72_CBBB1B20-A9F9-4E34-ABFE-213C012DB43E.png";

export default function Hero({ data, site }) {
  const badges = (data?.badges || []).filter((b) => b);
  const eyebrow = data?.eyebrow || "Welcome to M&C Clean Fresh";
  const heroImage = data?.image || NEW_HERO_IMAGE;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-white"
    >
      {/* Background photo (full-bleed) */}
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="M&C Clean Fresh team in front of branded moving truck"
          className="w-full h-full object-cover object-center"
          draggable={false}
        />
        {/* Left-side white wash so the copy stays readable.
            Strong opaque white on the left ~45%, then fades into the photo. */}
        <div
          className="absolute inset-0 hidden lg:block pointer-events-none"
          style={{
            background:
              "linear-gradient(to right, #ffffff 0%, #ffffff 40%, rgba(255,255,255,0.95) 50%, rgba(255,255,255,0.6) 60%, rgba(255,255,255,0.2) 70%, rgba(255,255,255,0) 80%)",
          }}
        />
        {/* Mobile/tablet: nearly full white wash so the photo sits subtly behind the text */}
        <div
          className="absolute inset-0 lg:hidden pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.86) 55%, rgba(255,255,255,0.55) 100%)",
          }}
        />
      </div>

      {/* Decorative gold orb */}
      <div
        className="gold-orb a pointer-events-none"
        style={{ width: 280, height: 280, top: "12%", left: "-4%" }}
      />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-28 sm:pt-32 lg:pt-36 pb-16 lg:pb-24 min-h-[640px] lg:min-h-[680px] flex items-center">
        <div className="w-full lg:w-[62%] xl:w-[58%]">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-eyebrow"
          >
            <span className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#B8941F] font-bold">
              {eyebrow}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-4 font-display text-4xl sm:text-5xl lg:text-[2.85rem] xl:text-[3.25rem] font-extrabold leading-[1.08] tracking-tight text-[#0A0A0A]"
            data-testid="hero-headline"
          >
            {data?.headline_1 || "Professional Moving &"}{" "}
            <br className="hidden sm:block" />
            {data?.headline_2 || "Home Services"}{" "}
            {data?.headline_accent ? (
              <span className="text-gradient-gold">{data.headline_accent}</span>
            ) : (
              <span className="text-gradient-gold">You Can Trust</span>
            )}
          </motion.h1>

          {data?.subheadline !== "" && (
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-5 text-base sm:text-lg text-gray-700 max-w-xl leading-relaxed"
              data-testid="hero-subheadline"
            >
              {data?.subheadline ||
                "Moving, Cleaning, Junk Removal, Lock Installation and TV Mounting Services."}
            </motion.p>
          )}

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4"
          >
            <a
              href="#contact"
              className="btn-gold justify-center sm:justify-start"
              data-testid="hero-cta-quote"
            >
              {data?.primary_cta || "Get Free Estimate"}
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={site?.phone_href || "#"}
              className="btn-outline-gold justify-center sm:justify-start"
              data-testid="hero-cta-call"
            >
              <Phone className="w-4 h-4" />
              {data?.secondary_cta || "Call Now"}
            </a>
          </motion.div>

          {badges.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3"
              data-testid="hero-trust-badges"
            >
              {badges.map((b, i) => (
                <div
                  key={`${b.label}-${i}`}
                  className="inline-flex items-center gap-2"
                >
                  <span className="w-7 h-7 rounded-full bg-white border border-[#D4AF37]/40 shadow-sm flex items-center justify-center">
                    <Icon
                      name={b.icon}
                      className="w-3.5 h-3.5 text-[#B8941F]"
                    />
                  </span>
                  <span className="text-[13px] text-gray-800 font-semibold">
                    {b.label}
                  </span>
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
